import { useState, useEffect, useCallback } from 'react';
import agent from '../api/agent';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useAppSelector } from '../store/configureStore';

export function useEducationEffects<T extends Record<string, any>>({
  serviceId,
  manualServiceChange,
  setValue,
  watch,
  isRestoringDraft,
  draftRestorationRef,
  role = 'client' // Default to client, but allow 'admin'
}: {
  serviceId: number | undefined;
  manualServiceChange: boolean;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  isRestoringDraft: boolean;
  draftRestorationRef: React.RefObject<boolean>;
  role?: 'client' | 'admin';
}) {
  const [educationEffects, setEducationEffects] = useState<string[]>([]);
  const [openModal, setOpenModal] = useState(false);
  
  // Get user from Redux store to check if SuperAdmin
  const { user } = useAppSelector(state => state.account);
  const isSuperAdmin = user?.roles?.includes('SuperAdmin');

  // Fetch education effects based on role
  useEffect(() => {
    async function fetchAndSetEducationEffects(serviceId: number) {
      // Skip draft-related checks if not SuperAdmin
      const shouldSkipDueToRestoration = isSuperAdmin && (isRestoringDraft || draftRestorationRef.current);
      
      if (shouldSkipDueToRestoration) {
        console.log(`Skipping ${role === 'admin' ? 'Admin ' : ''}getEducationEffects due to draft restoration`);
        return;
      }

      try {
        console.log(`Calling ${role === 'admin' ? 'Admin ' : ''}getEducationEffects for serviceId:`, serviceId);
        
        // Use the appropriate agent based on role
        const response = role === 'admin'
          ? await agent.Admin.Orders.getEducationEffects(serviceId)
          : await agent.Client.Orders.getEducationEffects(serviceId);
          
        if (response && response.data) {
          setValue('educationEffects' as unknown as any, response.data as any, { shouldValidate: true });
          setEducationEffects(response.data.split(';'));
        } else if (response) {
          setValue('educationEffects' as unknown as any, response as any, { shouldValidate: true });
          setEducationEffects(response.split(';'));
        }
      } catch (error) {
        console.error(`Błąd podczas pobierania efektów edukacyjnych${role === 'admin' ? ' (Admin)' : ''}`, error);
      }
    }

    if (serviceId && manualServiceChange) {
      fetchAndSetEducationEffects(serviceId);
    }
  }, [serviceId, manualServiceChange, setValue, isRestoringDraft, draftRestorationRef, role, isSuperAdmin]);

  // Watch for education effects changes in the form
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'educationEffects') {
        setEducationEffects((value.educationEffects || '').split(';'));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // The rest of the component remains the same
  const handleOpenModal = useCallback(() => {
    const currentEffects = watch('educationEffects' as any) as string;
    setEducationEffects(currentEffects ? currentEffects.split(';') : []);
    setOpenModal(true);
  }, [watch]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const addNewEffect = useCallback(() => {
    setEducationEffects([...educationEffects, '']);
  }, [educationEffects]);

  const updateEffect = useCallback((index: number, value: string) => {
    const newValue = value.replace(/;/g, '');
    const updatedEffects = [...educationEffects];
    updatedEffects[index] = newValue;
    setEducationEffects(updatedEffects);
  }, [educationEffects]);

  const removeEffect = useCallback((index: number) => {
    const newEffects = educationEffects.filter((_, i) => i !== index);
    setEducationEffects(newEffects);
  }, [educationEffects]);

  const handleSaveEducationEffects = useCallback(() => {
    const filteredEffects = educationEffects
      .filter(effect => effect.trim() !== '')
      .map(effect => effect.replace(/^;|;$/g, ''));

    setValue('educationEffects' as unknown as any, filteredEffects.join(';') as any, { 
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true 
    });
    
    handleCloseModal();
  }, [educationEffects, setValue, handleCloseModal]);

  return {
    educationEffects,
    setEducationEffects,
    openModal,
    handleOpenModal,
    handleCloseModal,
    addNewEffect,
    updateEffect,
    removeEffect,
    handleSaveEducationEffects,
    isSuperAdmin // Return this flag so components can check it
  };
}